// =============================================================================
// PTEL - useAudit hook
// FB.08: Conecta audit.service con el usuario autenticado
// GA.07: logCoordEdit — registra ediciones manuales de coordenadas
// =============================================================================

import { useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import type { AuditAction } from '../services/firebase/audit.service'
import {
  logDocumentAction,
  logPatternAction,
  logInfrastructureAction,
} from '../services/firebase'

type AuditBefore = Record<string, unknown> | null | undefined
type AuditAfter = Record<string, unknown> | null | undefined

// -----------------------------------------------------------------------------
// GA.07: Tipos para edición de coordenadas
// -----------------------------------------------------------------------------

/** Fuente de la edición de coordenadas */
export type CoordEditSource = 'drag' | 'manual' | 'form'

/** Coordenada UTM30 ETRS89 (EPSG:25830) */
export interface CoordPoint {
  /** Coordenada X en metros */
  x: number
  /** Coordenada Y en metros */
  y: number
  /** Score de confianza 0-100 (opcional) */
  score?: number
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Hook que envuelve el servicio de auditoría inyectando automáticamente
 * uid y email del usuario autenticado.
 *
 * Si no hay usuario, las funciones son no-op (devuelven '').
 */
export function useAudit() {
  const { user } = useAuth()

  const logDocument = useCallback(
    async (
      action: AuditAction,
      entityId: string,
      municipalityINE: string,
      before?: AuditBefore,
      after?: AuditAfter,
    ): Promise<string> => {
      if (!user) return ''
      return logDocumentAction(action, entityId, user.uid, user.email ?? '', municipalityINE, before, after)
    },
    [user],
  )

  const logPattern = useCallback(
    async (
      action: AuditAction,
      entityId: string,
      municipalityINE: string,
      before?: AuditBefore,
      after?: AuditAfter,
    ): Promise<string> => {
      if (!user) return ''
      return logPatternAction(action, entityId, user.uid, user.email ?? '', municipalityINE, before, after)
    },
    [user],
  )

  const logInfrastructure = useCallback(
    async (
      action: AuditAction,
      entityId: string,
      municipalityINE: string,
      before?: AuditBefore,
      after?: AuditAfter,
    ): Promise<string> => {
      if (!user) return ''
      return logInfrastructureAction(action, entityId, user.uid, user.email ?? '', municipalityINE, before, after)
    },
    [user],
  )

  /**
   * GA.07 — Registra una edición manual de coordenadas en Firestore.
   *
   * Escribe en `auditLog` con entityType='infrastructure' y action='update'.
   * El payload before/after preserva x, y, score para futura migración a PostGIS
   * (delta_m se calculará allí como columna generada ST_Distance).
   *
   * Fallo silencioso: si Firebase no responde, no interrumpe el flujo de la app.
   *
   * @param infraId    - ID único de la infraestructura
   * @param infraNombre - Nombre legible (para trazabilidad en el viewer)
   * @param ine5       - Código INE del municipio (5 dígitos)
   * @param before     - Coordenadas anteriores a la edición
   * @param after      - Coordenadas posteriores a la edición
   * @param source     - Origen de la edición: 'drag' | 'manual' | 'form'
   * @returns          - ID del documento en Firestore ('' si no hay usuario o falla)
   */
  const logCoordEdit = useCallback(
    async (
      infraId: string,
      infraNombre: string,
      ine5: string,
      before: CoordPoint,
      after: CoordPoint,
      source: CoordEditSource,
    ): Promise<string> => {
      if (!user) return ''
      return logInfrastructureAction(
        'update',
        infraId,
        user.uid,
        user.email ?? '',
        ine5,
        { x: before.x, y: before.y, score: before.score ?? null, infraNombre },
        { x: after.x, y: after.y, score: after.score ?? null, infraNombre, editSource: source },
      )
    },
    [user],
  )

  return { logDocument, logPattern, logInfrastructure, logCoordEdit }
}
